import { Box, Checkbox } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { materialStateEnum, stepModeEnum } from '../../constants';
import { useCreateDailyMachineMutation, useDailyMachineMutation, useDeleteDailyMachineMutation } from '../../hooks/mutations/useDailyMachineMutation';
import { useDailyResource } from '../../hooks/useDailyResource';
import { DataItem } from '../../services/apiSLTP/dailyReports/activities';
import { DailyMachine } from '../../services/apiSLTP/dailyReports/dailyMachines';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DraggableCell from '../dnd/DraggableCell';
import DroppableCell from '../dnd/DroppableCell';
import ManageDailyMachineModal from '../modals/operations/planning/ManageDailyMachineModal';
import IconButton from '../ui/IconButton';
import { DND_ITEM_TYPES } from '../../constants/dnd';

interface MachinesCellProps {
    activity: DataItem;
    activities: DataItem[];
    activityIndex: number;
    disabled?: boolean;
    refetch?: () => void;
    hiddenPlusIcon?: boolean;
    createTransfer?: boolean;
}

const MachinesCell: React.FC<MachinesCellProps> = ({
    activity,
    activityIndex,
    activities,
    disabled = false,
    refetch = () => {},
    hiddenPlusIcon = false,
    createTransfer = true,
}) => {
    const { t } = useTranslation();
    const itemType = DND_ITEM_TYPES.MACHINES;

    const { mutate: updateDailyMachine } = useDailyMachineMutation({ onSuccess: refetch });
    const { mutate: deleteDailyMachine } = useDeleteDailyMachineMutation({ onSuccess: refetch });
    const { mutate: createDailyMachine } = useCreateDailyMachineMutation({ onSuccess: refetch });

    const { selectedItem, manageModal, handleMove, handleDelete, handleDuplicate, openModal } = useDailyResource<DailyMachine>({
        onMove: async (fromLineId, itemId, toLineId) => {
            updateDailyMachine({ id: itemId, body: { activityId: toLineId, createTransfer: true }, queries: { deleteTransfers: true } });
        },
        onDelete: async (itemId) => {
            deleteDailyMachine({ id: itemId, queries: { deleteTransfers: true } });
        },
        onDuplicate: async (machine) => {
            createDailyMachine({
                activityId: activity._id,
                machineId: machine.machineId?._id,
                state: machine.state,
                createTransfer,
                isUsed: machine.isUsed,
                quantity: machine.quantity,
            });
        },
    });

    const countDuplicates = (machineId: string) => {
        const matches = activities.flatMap((a) => a.dailyMachines).filter((m) => m.machineId?._id === machineId);

        if (matches.length === 1) return 1;
        if (
            matches.length === 2 &&
            matches.some((m) => m.state === materialStateEnum.NEW) &&
            matches.some((m) => m.state === materialStateEnum.REMOVE)
        )
            return 1;

        return matches.length;
    };

    const showCheckbox = activity.stepMode === stepModeEnum.REPORT || activity.stepMode === stepModeEnum.VALIDATE;

    return (
        <Box sx={{ display: 'flex', height: '100%', width: '100%', flex: 1, opacity: disabled ? 0.7 : 1, color: 'black' }}>
            <DroppableCell dropId={{ lineId: activity._id }} moveCell={handleMove} itemType={itemType} disabled={disabled}>
                <div className="fx-r pos-re fx-fg-1 gap-5px">
                    <div>
                        {!hiddenPlusIcon && (
                            <IconButton
                                iconName="addCircle"
                                size="small"
                                onClick={() => openModal(null)}
                                disabled={disabled}
                                title={capitalizeFirstLetter(t('addMachine'))}
                            />
                        )}
                    </div>
                    <div className="gap-5px fx-c fx-fg-1">
                        {activity.dailyMachines.map((machine, index) => {
                            const duplicateCount = countDuplicates(machine.machineId?._id);

                            return (
                                <div key={`${activityIndex}-${index}`} className="gap-5px fx-r fx-fg-1 fx-items-fs">
                                    <DraggableCell
                                        dragIds={{ itemId: machine._id, lineId: activity._id }}
                                        index={index}
                                        itemType={itemType}
                                        onDelete={() => handleDelete(machine._id)}
                                        disabled={disabled}
                                        state={machine.state}
                                        onDuplicate={() => handleDuplicate(machine)}
                                        warning={duplicateCount > 1}
                                        warningChar={duplicateCount.toString()}
                                        onDoubleClick={() => openModal(machine)}
                                    >
                                        <div className="one-line-container">
                                            <div className="one-line" title={machine.machineId?.designation}>
                                                {machine.machineId?.designation}
                                            </div>
                                            {activity.stepMode === stepModeEnum.VALIDATE && (
                                                <div className="one-line" title={machine.machineId?.codeProC}>
                                                    {machine.machineId?.codeProC}
                                                </div>
                                            )}
                                        </div>
                                    </DraggableCell>

                                    {showCheckbox && (
                                        <div className="h-25px fx-r fx-items-c" title={capitalizeFirstLetter(t('isUsed'))}>
                                            <Checkbox
                                                checked={machine.isUsed}
                                                onChange={(_, checked) => {
                                                    updateDailyMachine(
                                                        { id: machine._id, body: { isUsed: checked } },
                                                        { onSuccess: () => refetch() }
                                                    );
                                                }}
                                                size="small"
                                                color="success"
                                                sx={{ padding: 0 }}
                                                disabled={disabled || !activity.includedInExpenses}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DroppableCell>

            {manageModal.value && (
                <ManageDailyMachineModal
                    open={manageModal.value}
                    handleClose={manageModal.setFalse}
                    refetch={refetch}
                    activityId={activity._id}
                    dailyMachine={selectedItem}
                    machine={null}
                    activities={activities}
                    createTransfer={createTransfer}
                    stepMode={activity.stepMode}
                />
            )}
        </Box>
    );
};

export default MachinesCell;
